/**
 * Localised copy + stem templates used by `generateMcqFromConcept`.
 *
 * Adding a new locale (A.7): create a new entry in `COPY` with the same `titles` and
 * `blurbs` keys as `en`. `getCopy(locale)` falls back to `en` for any missing locale.
 *
 * `stems` is an array of templates that take a `blurb` string and return the question
 * body. The generator picks one per question (seeded RNG) so the same concept does not
 * always produce the same wording.
 */

export type Locale = "en" | "es" | "el";

export const DEFAULT_LOCALE: Locale = "en";

/** Locales with a full `COPY` entry (for UI pickers). */
export const SUPPORTED_LOCALES: readonly Locale[] = ["en", "es", "el"];

export type StemTemplate = (blurb: string) => string;

export type LocaleStrings = {
  titles: Readonly<Record<string, string>>;
  blurbs: Readonly<Record<string, string>>;
  stems: readonly StemTemplate[];
};

const EN: LocaleStrings = {
  titles: {
    foundations: "What AI chat tools are, and are not",
    trust_reasoning: "Truth, confidence, and “sounds right”",
    privacy: "What you paste, where it can go",
    prompting: "Clear asks: goal, format, and audience",
    context: "Context limits, system vs user, long pastes",
    evidence: "Checking claims and using sources",
    system_safety: "Policies, refusals, and injection",
    tools_agents: "Multi-step work and using tools",
    retrieval: "Search, documents, and grounding",
    adversarial: "Jailbreaks, scams, and hard cases",
    model_theory: "How models differ from databases",
    research_frontier: "Limits, alignment, and trade-offs",
    bias_ethics: "Bias, attribution, and AI ethics",
    deepfakes_scams: "Deepfakes and AI-powered scams",
    verification_workflow: "Checking AI work before you trust it",
  },
  blurbs: {
    foundations:
      "The topic about what a text assistant is for, who decides when it’s wrong, and basic habits (short asks, re-read before you share).",
    trust_reasoning:
      "The topic about confident-but-wrong answers, verifying important facts, and what “sounds like a person” really means.",
    privacy:
      "The topic about passwords, sensitive data, and what a service might store or review under its rules.",
    prompting:
      "The topic about giving enough detail—goal, audience, format—so the system doesn’t have to guess.",
    context:
      "The topic about how much text can be considered at once, system rules vs your message, and what happens in very long pastes.",
    evidence:
      "The topic about asking for sources, double-checking high-stakes claims, and not treating prose as a citation.",
    system_safety:
      "The topic about how products block some requests, when refusal is a feature, and tricks that smuggle in bad instructions.",
    tools_agents:
      "The topic about breaking work into steps, when tools (search, code, email) are involved, and error compounding in chains.",
    retrieval:
      "The topic about documents, RAG, freshness, and quotes or citations the product actually supports.",
    adversarial:
      "The topic about social engineering, fake urgency, and attacks meant to make you leak data or break rules.",
    model_theory:
      "The topic about next-token training, plausibility vs truth, and how specialist models can beat huge generalists on one task.",
    research_frontier:
      "The topic about alignment, eval limits, and why latency and real-world tool feedback change agent design.",
    bias_ethics:
      "The topic about how AI can amplify bias from training data, who's accountable for AI decisions, when to disclose AI assistance, and the energy cost of large models.",
    deepfakes_scams:
      "The topic about synthetic voices, videos, and AI-written phishing — and using out-of-band checks (call back on a known number, agree on a code word) to verify before acting.",
    verification_workflow:
      "The topic about asking the AI for sources, asking what it might be wrong about, confirming with at least one independent source, and recognising when not to use AI at all.",
  },
  stems: [
    (b) => `Which topic best matches this description?\n\n“${b}”`,
    (b) => `Pick the topic this best describes:\n\n${b}`,
    (b) =>
      `If a friend explained an AI concept this way, what would they be talking about?\n\n«${b}»`,
    (b) => `You’re trying to remember a topic. Notes say:\n\n«${b}»\n\nWhich topic is it?`,
    (b) => `Which AI literacy concept does this describe?\n\n${b}`,
    (b) => `From the curriculum, this paragraph belongs to which topic?\n\n${b}`,
  ],
};

const ES: LocaleStrings = {
  titles: {
    foundations: "Qué son los asistentes de chat con IA, y qué no son",
    trust_reasoning: "Veracidad, confianza y lo que «suena bien»",
    privacy: "Lo que pegas y adónde puede acabar",
    prompting: "Pedidos claros: objetivo, formato y audiencia",
    context: "Límites de contexto, sistema frente a usuario, pegados largos",
    evidence: "Comprobar afirmaciones y usar fuentes",
    system_safety: "Políticas, negativas e inyección de instrucciones",
    tools_agents: "Trabajo en varios pasos y uso de herramientas",
    retrieval: "Búsqueda, documentos y anclar respuestas",
    adversarial: "Evasión de reglas, estafas y casos límite",
    model_theory: "Cómo difieren los modelos de las bases de datos",
    research_frontier: "Límites, alineación y equilibrios",
    bias_ethics: "Sesgo, atribución y ética de la IA",
    deepfakes_scams: "Deepfakes y estafas impulsadas por IA",
    verification_workflow: "Verificar lo que dice la IA antes de fiarse",
  },
  blurbs: {
    foundations:
      "El tema de para qué sirve un asistente de texto, quién decide cuándo se equivoca, y hábitos básicos (peticiones breves, releer antes de compartir).",
    trust_reasoning:
      "El tema de las respuestas con mucha confianza pero erróneas, de verificar lo importante y de qué significa realmente que algo «suene a persona».",
    privacy:
      "El tema de contraseñas, datos sensibles y qué puede guardar o revisar un servicio según sus reglas.",
    prompting:
      "El tema de dar el detalle suficiente (objetivo, audiencia, formato) para que el sistema no tenga que adivinar.",
    context:
      "El tema de cuánto texto se puede considerar a la vez, reglas de sistema frente a tu mensaje, y qué pasa con pegados muy largos.",
    evidence:
      "El tema de pedir fuentes, comprobar afirmaciones de alto riesgo y no tratar un texto bonito como si fuera una cita real.",
    system_safety:
      "El tema de cómo los productos bloquean ciertas peticiones, cuándo el rechazo es una función, y trucos que cuelan malas instrucciones.",
    tools_agents:
      "El tema de partir el trabajo en pasos, cuándo entran herramientas (búsqueda, código, correo) y cómo se acumulan los errores en cadenas.",
    retrieval:
      "El tema de documentos, RAG, novedad de la información, y citas o referencias que el producto realmente admite.",
    adversarial:
      "El tema de ingeniería social, urgencia falsa, y ataques para que filtres datos o incumplas reglas.",
    model_theory:
      "El tema del entrenamiento token a token, plausibilidad frente a verdad, y cómo modelos especializados baten a generalistas enormes en una tarea.",
    research_frontier:
      "El tema de alineación, límites de evaluación, y por qué la latencia y la retroalimentación con herramientas reales cambian el diseño de agentes.",
    bias_ethics:
      "El tema de cómo la IA puede amplificar sesgos de los datos de entrenamiento, quién es responsable de sus decisiones, cuándo revelar que se usó IA, y el coste energético de los grandes modelos.",
    deepfakes_scams:
      "El tema de voces y vídeos sintéticos y de la suplantación escrita por IA — y de verificar por un canal aparte (llamar a un número conocido, acordar una palabra clave) antes de actuar.",
    verification_workflow:
      "El tema de pedirle a la IA sus fuentes, preguntarle en qué podría equivocarse, confirmar con al menos una fuente independiente, y reconocer cuándo no usar IA.",
  },
  stems: [
    (b) => `¿Qué tema encaja mejor con esta descripción?\n\n«${b}»`,
    (b) => `Elige el tema que mejor describe esto:\n\n${b}`,
    (b) =>
      `Si un amigo explicara un concepto sobre IA de esta forma, ¿de qué estaría hablando?\n\n«${b}»`,
    (b) => `Quieres recordar un tema. Tus notas dicen:\n\n«${b}»\n\n¿De qué tema se trata?`,
    (b) => `¿Qué idea de formación en IA describe esto?\n\n${b}`,
    (b) => `En el temario, este párrafo corresponde a qué tema:\n\n${b}`,
  ],
};

const EL: LocaleStrings = {
  titles: {
    foundations: "Τι είναι (και τι δεν είναι) τα AI chat εργαλεία",
    trust_reasoning: "Αλήθεια, βεβαιότητα και τι «ακούγεται σωστό»",
    privacy: "Τι επικολλάς και πού μπορεί να καταλήξει",
    prompting: "Καθαρά prompts: στόχος, μορφή, κοινό",
    context: "Όρια context, κανόνες συστήματος vs χρήστη, μεγάλα κείμενα",
    evidence: "Έλεγχος ισχυρισμών και χρήση πηγών",
    system_safety: "Πολιτικές, αρνήσεις και prompt injection",
    tools_agents: "Πολυβηματική εργασία και χρήση εργαλείων",
    retrieval: "Αναζήτηση, έγγραφα και τεκμηρίωση απαντήσεων",
    adversarial: "Jailbreaks, απάτες και δύσκολες περιπτώσεις",
    model_theory: "Πώς διαφέρουν τα μοντέλα από βάσεις δεδομένων",
    research_frontier: "Όρια, ευθυγράμμιση και συμβιβασμοί",
    bias_ethics: "Μεροληψία, απόδοση και ηθική της AI",
    deepfakes_scams: "Deepfakes και απάτες με χρήση AI",
    verification_workflow: "Έλεγχος απαντήσεων AI πριν τις εμπιστευτείς",
  },
  blurbs: {
    foundations:
      "Το θέμα για το πού βοηθά ένας βοηθός κειμένου, ποιος αποφασίζει πότε κάνει λάθος, και βασικές συνήθειες (σύντομα αιτήματα, επανέλεγχος πριν κοινοποιήσεις).",
    trust_reasoning:
      "Το θέμα για απαντήσεις με αυτοπεποίθηση αλλά λάθος, για επαλήθευση κρίσιμων πληροφοριών, και για το τι σημαίνει πραγματικά ότι κάτι «μοιάζει ανθρώπινο».",
    privacy:
      "Το θέμα για κωδικούς, ευαίσθητα δεδομένα και τι μπορεί να αποθηκεύει ή να ελέγχει μια υπηρεσία σύμφωνα με τους όρους της.",
    prompting:
      "Το θέμα για το να δίνεις αρκετές λεπτομέρειες — στόχο, κοινό, μορφή — ώστε το σύστημα να μη μαντεύει.",
    context:
      "Το θέμα για το πόσο κείμενο χωράει στο context, για κανόνες συστήματος έναντι χρήστη, και τι γίνεται με πολύ μεγάλες επικολλήσεις.",
    evidence:
      "Το θέμα για το να ζητάς πηγές, να επιβεβαιώνεις ισχυρισμούς υψηλού ρίσκου και να μην αντιμετωπίζεις την καλή διατύπωση ως απόδειξη.",
    system_safety:
      "Το θέμα για το πώς τα προϊόντα μπλοκάρουν κάποια αιτήματα, πότε η άρνηση είναι χαρακτηριστικό ασφαλείας, και τεχνάσματα που περνούν κακόβουλες οδηγίες.",
    tools_agents:
      "Το θέμα για διάσπαση εργασίας σε βήματα, για χρήση εργαλείων (αναζήτηση, κώδικας, email) και για συσσώρευση λαθών σε αλυσίδες.",
    retrieval:
      "Το θέμα για έγγραφα, RAG, επικαιρότητα γνώσης, και ποιες παραπομπές υποστηρίζει πραγματικά το προϊόν.",
    adversarial:
      "Το θέμα για social engineering, ψεύτικη επείγουσα ανάγκη, και επιθέσεις που προσπαθούν να σε κάνουν να διαρρεύσεις δεδομένα ή να παραβείς κανόνες.",
    model_theory:
      "Το θέμα για εκπαίδευση token-by-token, πιθανότητα έναντι αλήθειας, και γιατί εξειδικευμένα μοντέλα μπορεί να ξεπερνούν πολύ μεγάλα γενικά μοντέλα σε συγκεκριμένη εργασία.",
    research_frontier:
      "Το θέμα για ευθυγράμμιση, όρια αξιολόγησης, και γιατί η καθυστέρηση και το feedback από πραγματικά εργαλεία αλλάζουν το design των agents.",
    bias_ethics:
      "Το θέμα για το πώς η AI μπορεί να ενισχύσει μεροληψίες των δεδομένων εκπαίδευσης, ποιος έχει ευθύνη για αποφάσεις AI, πότε πρέπει να δηλώνεται η χρήση AI, και το ενεργειακό κόστος μεγάλων μοντέλων.",
    deepfakes_scams:
      "Το θέμα για συνθετικές φωνές/βίντεο και phishing με AI — και για επαλήθευση από ανεξάρτητο κανάλι (επιστροφή κλήσης σε γνωστό αριθμό, λέξη-κλειδί) πριν ενεργήσεις.",
    verification_workflow:
      "Το θέμα για το να ζητάς πηγές από την AI, να ρωτάς πού μπορεί να κάνει λάθος, να επιβεβαιώνεις με τουλάχιστον μία ανεξάρτητη πηγή, και να αναγνωρίζεις πότε δεν πρέπει να χρησιμοποιήσεις AI.",
  },
  stems: [
    (b) => `Ποιο θέμα ταιριάζει καλύτερα με αυτή την περιγραφή;\n\n«${b}»`,
    (b) => `Επίλεξε το θέμα που περιγράφει καλύτερα αυτό:\n\n${b}`,
    (b) => `Αν ένας φίλος εξηγούσε έτσι μια έννοια AI, για ποιο θέμα θα μιλούσε;\n\n«${b}»`,
    (b) => `Προσπαθείς να θυμηθείς ένα θέμα. Οι σημειώσεις λένε:\n\n«${b}»\n\nΠοιο είναι;`,
    (b) => `Ποια έννοια AI literacy περιγράφει αυτό;\n\n${b}`,
    (b) => `Στο πρόγραμμα σπουδών, αυτή η παράγραφος ανήκει σε ποιο θέμα;\n\n${b}`,
  ],
};

export const COPY: Readonly<Record<Locale, LocaleStrings>> = { en: EN, es: ES, el: EL };

export function getCopy(locale: Locale = DEFAULT_LOCALE): LocaleStrings {
  return COPY[locale] ?? EN;
}

// --- Back-compat exports (pre-A.7) -----------------------------------------
// Keep the old shapes alive so callers don’t have to change.
export const CONCEPT_TITLES: Readonly<Record<string, string>> = EN.titles;
export const CONCEPT_BLURBS: Readonly<Record<string, string>> = EN.blurbs;

export function allConceptCopyIds(): string[] {
  return Object.keys(EN.titles);
}
